import React from 'react'
import { DateTime } from '../models/DateTime/DateTime'
import { Double } from '../models/Double/Double'
import { Duration } from '../models/Duration/Duration'
import { Enum } from '../models/Enum/Enum'
import { Flag } from '../models/Flag/Flag'
import { Integer } from '../models/Integer/Integer'
import { Secret } from '../models/Secret/Secret'
import { StringModel } from '../models/StringModel/StringModel'
import { ModelTypes } from '../models/types'
import { InputViewOnly } from '../ViewOnly/InputViewOnly'
import { getIsViewOnly } from '../../utils/viewOnly'
import { Text } from '../models/Text/Text'
import { buildActionsUI } from '../ActionsBar/buildActionsUI'
import { BlockLazy as Block } from '../models/Block/BlockLazy'
import { WrappedBlockLazy as WrappedBlock } from '../models/Block/WrappedBlockLazy'
import { BlockArrayLazy as BlockArray} from '../models/BlockArray/BlockArrayLazy'
import { BlockDictionaryLazy as BlockDictionary } from '../models/BlockDictionary/BlockDictionaryLazy'
import { PATH_JOINER } from '../models/utils/getConfigBitPath'
import { Num } from '../models/Num/Num'
import { IntegerRange } from '../models/IntegerRange/IntegerRange'
import { SuggestedValues } from '../models/SuggestedValues/SuggestedValues'

interface IElements {
  [key: string]: JSX.Element
}

export function buildElementsExt(
  path: string,
  name: string,
  ProductScheme: any = {},
  ProductConfiguration: any = {},
  wrapper?: string,
  isParentDisabled?: boolean,
  levelDeep?: number,
  element_id?: string,
  inCard?: boolean,
  isRuleActive?: boolean,
  pathWithElementID?: string,
): JSX.Element {
  const elementType: string = ProductScheme?.type || 'default'
  const isViewOnly = getIsViewOnly(ProductScheme.is_access_restricted)
  const isGeneratedByProduct = ProductScheme.is_generated_by_product
  const hasSuggestedValues = !!ProductScheme.suggested_values_path
  if (isGeneratedByProduct) return <></>

  const elements: IElements = {
    [ModelTypes.BLOCK]: !wrapper ? (
      <Block
        key={path + pathWithElementID + name}
        dataPath={path}
        pathWithElementID={pathWithElementID}
        title={ProductScheme?.name}
        dataSchema={ProductScheme}
        name={name}
        wrapper={wrapper || ''}
        isParentDisabled={isParentDisabled}
        isViewOnly={isViewOnly}
        element_id={element_id}
        inCard={inCard}
        isRuleActive={isRuleActive}
      />
    ) : (
      <WrappedBlock
        key={path + pathWithElementID + name}
        dataPath={path}
        pathWithElementID={pathWithElementID}
        title={ProductScheme?.name}
        dataSchema={ProductScheme}
        name={name}
        wrapper={wrapper || 'block'}
        isParentDisabled={isParentDisabled}
        levelDeep={levelDeep}
        isViewOnly={isViewOnly}
        element_id={element_id}
        inCard={inCard}
        isRuleActive={isRuleActive}
      />
    ),
    [ModelTypes.BLOCK_ARRAY]: (
      <BlockArray
        key={path + pathWithElementID + name}
        dataPath={path}
        pathWithElementID={pathWithElementID}
        schema={ProductScheme}
        title={name}
        wrapper={wrapper}
        levelDeep={levelDeep}
        isViewOnly={isViewOnly}
        element_id={element_id}
        inCard={inCard}
        isRuleActive={isRuleActive}
        isParentDisabled={isParentDisabled}
      />
    ),
    [ModelTypes.BLOCK_DICTIONARY]: (
      <BlockDictionary
        key={path + pathWithElementID + name}
        dataPath={path}
        pathWithElementID={pathWithElementID}
        wrapper={wrapper}
        dataSchema={ProductScheme}
        name={name}
        isViewOnly={isViewOnly}
        element_id={element_id}
        inCard={inCard}
        isRuleActive={isRuleActive}
      />
    ),
    [ModelTypes.FLAG]: (
      <Flag
        dataPath={path}
        dataSchema={ProductScheme}
        name={name}
        isViewOnly={isViewOnly}
        key={name + path}
      />
    ),
    [ModelTypes.BOOL]: (
      <Flag
        dataPath={path}
        dataSchema={ProductScheme}
        name={name}
        isViewOnly={isViewOnly}
        key={name + path}
      />
    ),
    [ModelTypes.ENUM]: isViewOnly ? (
      <InputViewOnly
        name={name}
        title={ProductScheme.name}
        value={ProductConfiguration?.[name]}
        key={name + path}
      />
    ) : (
      <Enum
        dataPath={path}
        dataSchema={ProductScheme}
        name={name}
        wrapper={wrapper || ''}
        key={name + path}
      />
    ),
    [ModelTypes.INTEGER]: isViewOnly ? (
      <InputViewOnly
        name={name}
        title={ProductScheme.name}
        value={ProductConfiguration?.[name]}
        key={name + path}
      />
    ) : (
      <Integer
        key={path + pathWithElementID + name}
        dataPath={path}
        dataSchema={ProductScheme}
        name={name}
        wrapper={wrapper || ''}
        pathWithElementID={pathWithElementID}
      />
    ),
    [ModelTypes.INTEGER_RANGE]: isViewOnly ? (
      <InputViewOnly
        name={name}
        title={ProductScheme.name}
        value={ProductConfiguration?.[name]}
        key={name + path}
      />
    ) : (
      <IntegerRange
        key={path + pathWithElementID + name}
        dataPath={path}
        dataSchema={ProductScheme}
        name={name}
        wrapper={wrapper || ''}
        pathWithElementID={pathWithElementID}
      />
    ),
    [ModelTypes.NUMBER]: isViewOnly ? (
      <InputViewOnly
        name={name}
        title={ProductScheme.name}
        value={ProductConfiguration?.[name]}
        key={name + path}
      />
    ) : (
      <Num
        key={path + pathWithElementID + name}
        dataPath={path}
        pathWithElementID={pathWithElementID}
        dataSchema={ProductScheme}
        name={name}
        wrapper={wrapper || ''}
      />
    ),
    [ModelTypes.STRING]: isViewOnly ? (
      <InputViewOnly
        name={name}
        title={ProductScheme.name}
        value={ProductConfiguration?.[name]}
        key={name + path}
      />
    ) : hasSuggestedValues ? (
        <SuggestedValues
            dataPath={path}
            dataSchema={ProductScheme}
            name={name}
            wrapper={wrapper || ''}
            pathWithElementID={pathWithElementID}
        />
    ) : (
      <StringModel
        key={path + pathWithElementID + name}
        dataPath={path}
        dataSchema={ProductScheme}
        name={name}
        wrapper={wrapper || ''}
        pathWithElementID={pathWithElementID}
      />
    ),
    [ModelTypes.TEXT]: isViewOnly ? (
      <InputViewOnly
        name={name}
        title={ProductScheme.name}
        value={ProductConfiguration?.[name]}
        key={name + path}
      />
    ) : (
      <Text
        key={path + pathWithElementID + name}
        dataPath={path}
        dataSchema={ProductScheme}
        name={name}
        wrapper={wrapper || ''}
        pathWithElementID={pathWithElementID}
      />
    ),
    [ModelTypes.VERSION]: isViewOnly ? (
      <InputViewOnly
        name={name}
        title={ProductScheme.name}
        value={ProductConfiguration?.[name]}
        key={name + path}
      />
    ) : (
      <StringModel
        key={path + pathWithElementID + name}
        dataPath={path}
        dataSchema={ProductScheme}
        name={name}
        wrapper={wrapper || ''}
        pathWithElementID={pathWithElementID}
      />
    ),
    [ModelTypes.SECRET]: isViewOnly ? (
      <InputViewOnly
        type={ModelTypes.SECRET}
        name={name}
        title={ProductScheme.name}
        value={ProductConfiguration?.[name]}
        key={name + path}
      />
    ) : (
      <Secret
        key={path + pathWithElementID + name}
        dataPath={path}
        dataSchema={ProductScheme}
        name={name}
        wrapper={wrapper || ''}
        pathWithElementID={pathWithElementID}
      />
    ),
    [ModelTypes.DATETIME]: isViewOnly ? (
      <InputViewOnly
        name={name}
        title={ProductScheme.name}
        value={ProductConfiguration?.[name]}
        key={name + path}
      />
    ) : (
      <DateTime
        key={path + pathWithElementID + name}
        dataPath={path}
        dataSchema={ProductScheme}
        name={name}
        isParentDisabled={isParentDisabled}
        pathWithElementID={pathWithElementID}
      />
    ),
    [ModelTypes.TIME]: isViewOnly ? (
      <InputViewOnly
        name={name}
        title={ProductScheme.name}
        value={ProductConfiguration?.[name]}
        key={name + path}
      />
    ) : (
      <DateTime
        key={path + pathWithElementID + name}
        dataPath={path}
        dataSchema={ProductScheme}
        name={name}
        isParentDisabled={isParentDisabled}
        pathWithElementID={pathWithElementID}
      />
    ),
    [ModelTypes.DOUBLE]: isViewOnly ? (
      <InputViewOnly
        name={name}
        title={ProductScheme.name}
        value={ProductConfiguration?.[name]}
        key={name + path}
      />
    ) : (
      <Double
        key={path + pathWithElementID + name}
        dataPath={path}
        dataSchema={ProductScheme}
        name={name}
        wrapper={wrapper || ''}
        pathWithElementID={pathWithElementID}
      />
    ),
    [ModelTypes.DURATION]: isViewOnly ? (
      <InputViewOnly
        name={name}
        title={ProductScheme.name}
        value={ProductConfiguration?.[name]}
        key={name + path}
      />
    ) : (
      <Duration
        key={path + pathWithElementID + name}
        dataPath={path}
        dataSchema={ProductScheme}
        name={name}
        wrapper={wrapper || ''}
        pathWithElementID={pathWithElementID}
      />
    ),
    default: <></>,
  }

  return elements[elementType]
}

const getUiBuilder = (builderType: 'actionUiBuilder' | 'configUiBuilder') => {
  switch (builderType) {
      case 'actionUiBuilder': {
        return buildActionsUI
      }
    default: {
      return buildElementsExt
    }
  }
}

export const componentConstructor = ({
  path,
  pathWithElementID,
  ProductScheme = {},
  ProductConfiguration = {},
  name,
  wrapper,
  classname,
  elements,
  isParentDisabled,
  levelDeep,
  element_id,
  inCard,
  isRuleActive,
  builderType
}:{
  path: string,
  pathWithElementID: string | undefined,
  ProductScheme: any,
  ProductConfiguration: any,
  name: string,
  builderType: 'actionUiBuilder' | 'configUiBuilder',
  wrapper?: string,
  classname?: string,
  elements?: any,
  isParentDisabled?: boolean,
  levelDeep?: number,
  element_id?: string,
  inCard?: boolean,
  isRuleActive?: boolean,
}): JSX.Element => {
  let elementsGUI = elements || {}

  if (ProductScheme.children) {
    const keys = Object.keys(ProductScheme.children)
    const uiBuilder = getUiBuilder(builderType)

    for (const item in keys) {
      const element = uiBuilder(
        path,
        keys[item],
        ProductScheme.children[keys[item]],
        ProductConfiguration,
        wrapper,
        isParentDisabled,
        levelDeep,
        element_id,
        inCard,
        isRuleActive,
        pathWithElementID,
      )
      elementsGUI = {
        ...elementsGUI,
        [name]: { ...elementsGUI[name], [keys[item]]: [element] },
      }
    }
  }

  const keys = elementsGUI[name] ? Object.keys(elementsGUI[name]) : []
  return (
    <div className={`${classname}`}>
      {keys.map((el: string) => elementsGUI[name][el])}
    </div>
  )
}

export const routeConstructor = (
  ProductScheme: any = {},
  ProductConfiguration: any = {},
  name: string,
  guid: string,
): JSX.Element => {
  const path= [guid, 'configuration', name].join(PATH_JOINER)

  return (
    <div>
      {componentConstructor({
        path,
        pathWithElementID: undefined,
        ProductScheme: ProductScheme[name],
        ProductConfiguration: ProductConfiguration[name],
        name,
        wrapper: '',
        classname: 'd-flex flex-wrap min-500 flex-container',
        builderType: 'configUiBuilder'
      })}
    </div>
  )
}
